import React from 'react';
import {
    HardHat,
    ArrowUpRight,
    ShieldCheck,
    Building2,
    Accessibility,
    DraftingCompass,
    Sparkles,
    CalendarDays,
    FileText,
    Users,
} from 'lucide-react';
import { useTheme } from '../../../context/ThemeContext';

// External ProSpec360 app. Swap this when the endpoint changes; nothing else
// in this file needs to be touched.
const PROSPEC_URL = 'https://cqa-client.vercel.app/';

// The five inspection methodologies, grouped by the two departments that run them.
const DEPARTMENTS = [
    {
        name: 'Capital Inspections',
        services: [
            { icon: HardHat, label: 'CQA', desc: 'Construction Quality Assurance' },
            { icon: Building2, label: 'CNA', desc: 'Capital Needs Assessment' },
        ],
    },
    {
        name: 'Compliance Inspections',
        services: [
            { icon: ShieldCheck, label: 'NSPIRE', desc: 'HUD physical inspection' },
            { icon: Accessibility, label: 'Section 504', desc: 'Accessibility compliance' },
            { icon: DraftingCompass, label: 'Design Review', desc: 'Blueprint review' },
        ],
    },
];

// Capabilities that span every inspection type.
const PLATFORM = [
    { icon: Sparkles, label: 'JPRO assistance' },
    { icon: CalendarDays, label: 'Scheduling' },
    { icon: FileText, label: 'PDF reporting' },
    { icon: Users, label: 'Client portal' },
];

export default function ProSpec360() {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    return (
        <div className={`h-full overflow-y-auto ${isDark ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
            <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 sm:py-14">
                <div
                    className={`overflow-hidden rounded-2xl border ${
                        isDark
                            ? 'border-gray-700 bg-gray-800 shadow-xl shadow-black/40'
                            : 'border-gray-200 bg-white shadow-xl shadow-blue-900/10'
                    }`}
                >
                    {/* Brand hero: deep blue field, always dark in both themes */}
                    <div className="relative overflow-hidden bg-[#0B1B3B] px-6 py-10 sm:px-12 sm:py-12">
                        <div
                            aria-hidden="true"
                            className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-blue-500/20 blur-3xl"
                        />
                        <div className="relative">
                            <p className="font-unna text-xs uppercase tracking-[0.25em] text-blue-300">
                                JPRO &middot; Inspection Intelligence
                            </p>
                            <div className="mt-5 h-px w-12  bg-orange-400" />
                            <h1 className="mt-3 font-dm-serif text-5xl text-center text-white sm:text-6xl">
                                ProSpec360
                            </h1>
                            
                            <p className="mt-5 max-w-xl font-rasa text-lg leading-relaxed text-blue-100/90">
                                An enterprise quality assurance and compliance inspection
                                platform, spanning five specialized methodologies across
                                two departments.
                            </p>
                        </div>

                        {/* Signature: the full 360 degrees of coverage, 360 set in orange */}
                        <div aria-hidden="true" className="relative mt-10 hidden select-none sm:block">
                            <p className="flex items-baseline justify-between font-dm-serif text-2xl text-blue-200/20">
                                <span>0</span>
                                <span>60</span>
                                <span>120</span>
                                <span>180</span>
                                <span>240</span>
                                <span>300</span>
                                <span className="text-orange-400/60">360</span>
                            </p>
                            <p className="mt-3 font-unna text-[10px] uppercase tracking-[0.3em] text-blue-200/60">
                                Full-circle inspection coverage
                            </p>
                        </div>
                    </div>

                    {/* Departments and their five methodologies */}
                    <div className="px-6 py-8 sm:px-12 sm:py-10">
                        <div className="space-y-8">
                            {DEPARTMENTS.map(({ name, services }) => (
                                <section key={name}>
                                    <div className="flex items-center gap-3">
                                        <span aria-hidden="true" className="h-px w-6 shrink-0 bg-orange-400" />
                                        <h2
                                            className={`font-unna text-sm font-bold uppercase tracking-[0.2em] ${
                                                isDark ? 'text-blue-300' : 'text-blue-700'
                                            }`}
                                        >
                                            {name}
                                        </h2>
                                        <span
                                            aria-hidden="true"
                                            className={`h-px flex-1 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}
                                        />
                                    </div>
                                    <div
                                        className={`mt-4 grid gap-3 sm:grid-cols-2 ${
                                            services.length === 3 ? 'lg:grid-cols-3' : ''
                                        }`}
                                    >
                                        {services.map(({ icon: Icon, label, desc }) => (
                                            <div
                                                key={label}
                                                className={`flex items-start gap-4 rounded-xl  p-4 transition-colors ${
                                                    isDark
                                                        ? ''
                                                        : ''
                                                }`}
                                            >
                                                <span
                                                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
                                                        isDark
                                                            ? ' text-blue-400'
                                                            : ' text-blue-700'
                                                    }`}
                                                >
                                                    <Icon className="h-5 w-5" />
                                                </span>
                                                <div className="min-w-0">
                                                    <span
                                                        className={`block font-unna text-lg font-bold leading-snug ${
                                                            isDark ? 'text-gray-100' : 'text-gray-900'
                                                        }`}
                                                    >
                                                        {label}
                                                    </span>
                                                    <span
                                                        className={`mt-0.5 block font-rasa text-base leading-snug ${
                                                            isDark ? 'text-gray-400' : 'text-gray-600'
                                                        }`}
                                                    >
                                                        {desc}
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            ))}
                        </div>

                        {/* Capabilities that ship with every inspection type */}
                        <div className={`mt-10 border-t pt-7 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <p
                                className={`font-unna text-xs uppercase tracking-[0.25em] ${
                                    isDark ? 'text-gray-400' : 'text-gray-500'
                                }`}
                            >
                                Across every inspection
                            </p>
                            <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                                {PLATFORM.map(({ icon: Icon, label }) => (
                                    <div key={label} className="flex items-center gap-2.5">
                                        <Icon
                                            className={`h-4 w-4 shrink-0 ${
                                                isDark ? 'text-blue-400' : 'text-blue-600'
                                            }`}
                                        />
                                        <span
                                            className={`font-rasa text-base ${
                                                isDark ? 'text-gray-300' : 'text-gray-700'
                                            }`}
                                        >
                                            {label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Hand-off to the ProSpec360 app */}
                        <div className={`mt-8 flex flex-wrap items-center gap-x-5 gap-y-3 border-t pt-7 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <a
                                href={PROSPEC_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`inline-flex items-center gap-2 rounded-lg px-7 py-3 font-unna text-base tracking-wide text-white transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                    isDark
                                        ? 'bg-blue-500 hover:bg-blue-400 focus:ring-offset-gray-800'
                                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-offset-white'
                                }`}
                            >
                                Continue to ProSpec360
                                <ArrowUpRight className="h-5 w-5" />
                            </a>
                            <p className={`font-rasa text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                                Opens in a new tab.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
